import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, brandStory, reputationList } from '@/mocks/site-info';

export default function About() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#contact') {
      const el = document.getElementById('contact');
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    }
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [location.hash]);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressCountry: 'CN',
      streetAddress: siteInfo.address,
    },
    foundingDate: siteInfo.foundingYear,
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: `了解${siteInfo.name}的品牌故事、资质信誉和联系方式`,
    url: `${siteInfo.url}/about`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description={`了解${siteInfo.name}的品牌故事、发展历程、资质认证、信誉保障以及联系方式。${siteInfo.description}`}
        keywords="51cg网关于我们,51cg网品牌介绍,51cg网联系方式,51cg网信誉,51cg网资质"
        canonicalUrl="https://www.51cg.com/about"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      <Navbar />

      <main className="pt-16">
        {/* Page Header */}
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-4">关于{siteInfo.name}</h1>
            <p className="text-foreground-500 text-sm mt-2">了解我们的品牌故事、资质信誉与联系方式</p>
          </div>
        </div>

        {/* Brand Story */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 items-start">
              <div className="lg:w-2/3">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">品牌故事</h2>
                <div className="text-foreground-700 text-sm leading-relaxed space-y-4">
                  {brandStory.trim().split('\n\n').map((paragraph, i) => (
                    <p key={i}>{paragraph.trim()}</p>
                  ))}
                </div>
              </div>
              <div className="lg:w-1/3 flex-shrink-0">
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20office%20team%20collaboration%20scene%20with%20warm%20lighting%2C%20professional%20creative%20workspace%2C%20clean%20minimalist%20design%2C%20soft%20natural%20light%2C%20editorial%20photography%20style%2C%20elegant%20composition&width=600&height=400&seq=about-brand&orientation=landscape"
                    alt={`${siteInfo.name}团队`}
                    className="w-full h-auto"
                    title={`${siteInfo.name}团队`}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Timeline */}
        <section className="py-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-10 text-center">发展历程</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { year: '2018', event: '51cg网正式成立\n平台上线运营' },
                { year: '2020', event: '用户突破100万\n功能全面升级' },
                { year: '2023', event: '用户突破500万\n荣获多项认证' },
                { year: '2025', event: '持续创新优化\n服务不断升级' },
              ].map((item) => (
                <div key={item.year} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-500 text-background-50 font-heading font-bold text-lg mb-3">
                    {item.year}
                  </div>
                  <p className="text-foreground-500 text-sm whitespace-pre-line leading-relaxed">{item.event}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-10 text-center">资质认证与信誉保障</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {reputationList.map((item) => (
                <div key={item.title} className="bg-background-100 rounded-lg p-5 border border-background-200/70 text-center">
                  <div className="w-10 h-10 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-3">
                    <i className="ri-verified-badge-line text-accent-600 text-lg"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-10 text-center">联系我们</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground-900 text-sm">电子邮箱</h3>
                      <p className="text-foreground-500 text-sm mt-0.5">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground-900 text-sm">客服电话</h3>
                      <p className="text-foreground-500 text-sm mt-0.5">{siteInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground-900 text-sm">公司地址</h3>
                      <p className="text-foreground-500 text-sm mt-0.5">{siteInfo.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-primary-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground-900 text-sm">工作时间</h3>
                      <p className="text-foreground-500 text-sm mt-0.5">{siteInfo.workingHours}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors">
                    <i className="ri-wechat-line text-foreground-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-foreground-500 text-xs">微信公众号</p>
                    <p className="text-foreground-700 text-sm font-medium">{siteInfo.wechat}</p>
                  </div>
                </div>
              </div>

              <div>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="py-10 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="rounded-lg overflow-hidden border border-background-200/70">
              <iframe
                title="51cg网位置"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3057.486676866112!2d116.30602331538725!3d39.98345297941787!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35f05152a1bd008b%3A0x3d04f3b5e5fe1115!2sZhongguancun%2C%20Haidian%2C%20Beijing!5e0!3m2!1szh-CN!2scn!4v1690000000000!5m2!1szh-CN!2scn"
                width="100%"
                height="320"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
              ></iframe>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}

function ContactForm() {
  const formUrl = "https://readdy.ai/api/form/d9pi24li2vhsdh2d3520";

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value || '';
    if (honeypot.trim()) {
      const el = document.getElementById('contact-form-status');
      if (el) el.textContent = '提交成功！我们会尽快回复您。';
      return;
    }
    const formData = new FormData(form);
    const urlEncoded = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'phone_alt' || !(value as string).trim()) {
        urlEncoded.append(key, value as string);
      }
    });
    fetch(formUrl, {
      method: 'POST',
      body: urlEncoded,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })
      .then((res) => res.text())
      .then((text) => {
        try {
          const parsed = JSON.parse(text);
          if (parsed.code === 'OK') {
            const el = document.getElementById('contact-form-status');
            if (el) el.textContent = '提交成功！我们会尽快回复您。';
            form.reset();
            return;
          }
        } catch {
          /* ignore parse errors */
        }
        const el = document.getElementById('contact-form-status');
        if (el) el.textContent = '提交失败，请稍后重试。';
      })
      .catch(() => {
        const el = document.getElementById('contact-form-status');
        if (el) el.textContent = '提交失败，请稍后重试。';
      });
  };

  return (
    <form data-readdy-form onSubmit={handleSubmit} className="bg-background-50 rounded-lg p-6 border border-background-200/70 space-y-4">
      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">发送消息</h3>
      <div>
        <label htmlFor="contact-name" className="text-sm text-foreground-700 mb-1 block">姓名</label>
        <input
          id="contact-name"
          name="name"
          type="text"
          required
          className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
          placeholder="请输入您的姓名"
        />
      </div>
      <div>
        <label htmlFor="contact-email" className="text-sm text-foreground-700 mb-1 block">邮箱</label>
        <input
          id="contact-email"
          name="email"
          type="email"
          required
          className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
          placeholder="请输入您的邮箱"
        />
      </div>
      <div>
        <label htmlFor="contact-subject" className="text-sm text-foreground-700 mb-1 block">主题</label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          required
          className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
          placeholder="请输入消息主题"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="text-sm text-foreground-700 mb-1 block">消息内容</label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
          placeholder="请输入您的消息（500字以内）"
        ></textarea>
      </div>
      <div className="honeypot-wrap">
        <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden readOnly />
      </div>
      <button
        type="submit"
        className="w-full bg-primary-500 text-background-50 py-2.5 rounded-md text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
      >
        发送消息
      </button>
      <p id="contact-form-status" className="text-sm text-center text-foreground-500 min-h-[20px]"></p>
    </form>
  );
}